'use client'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Copy, Mail, MessageCircle, Share2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Tournament } from '@/lib/tournaments'

/**
 * Channels we can hand an invite off to. Each builds its own share URL from
 * the event link + a short blurb; all of them open in a new tab except SMS,
 * which hands off to the device's messaging app.
 */
type Channel = {
  key: string
  label: string
  icon: React.ReactNode
  href: (url: string, text: string) => string
  /** SMS/mail links must not open a blank tab on desktop. */
  sameTab?: boolean
}

const XIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="size-5">
    <path d="M18.2 2H21l-6.4 7.3L22.3 22h-6l-4.7-6.2L6.2 22H3.4l6.9-7.9L2 2h6.1l4.3 5.7L18.2 2Zm-1 18.3h1.6L7.9 3.6H6.2l11 16.7Z" />
  </svg>
)

const FacebookIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="size-5">
    <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12Z" />
  </svg>
)

const DiscordIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="size-5">
    <path d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.2.4c1.6.4 2.9 1 4.2 1.8a14 14 0 0 0-4.4-1.4 15.6 15.6 0 0 0-4-.1c-.2 0-.3 0-.5.1a14.4 14.4 0 0 0-4 1.4c1.2-.8 2.6-1.4 4.1-1.8L10.4 3a19.8 19.8 0 0 0-4.9 1.4C2.4 9 1.6 13.5 2 17.9a19.9 19.9 0 0 0 6 3l1.2-1.7c-1-.4-1.9-.9-2.7-1.5l.6-.4a14.2 14.2 0 0 0 12.1 0l.6.4c-.8.6-1.7 1.1-2.7 1.5l1.2 1.7a19.8 19.8 0 0 0 6-3c.5-5.1-.8-9.6-3.4-13.5ZM8.7 15.3c-1.2 0-2.1-1.1-2.1-2.4 0-1.3.9-2.4 2.1-2.4 1.2 0 2.2 1.1 2.2 2.4 0 1.3-1 2.4-2.2 2.4Zm6.6 0c-1.2 0-2.1-1.1-2.1-2.4 0-1.3.9-2.4 2.1-2.4 1.2 0 2.2 1.1 2.2 2.4 0 1.3-1 2.4-2.2 2.4Z" />
  </svg>
)

const TikTokIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="size-5">
    <path d="M16.6 2h-3.2v13.7a2.5 2.5 0 1 1-2.5-2.5c.3 0 .5 0 .7.1v-3.2a6 6 0 0 0-.7 0 5.6 5.6 0 1 0 5.6 5.6V8.9a7 7 0 0 0 4.1 1.3V7a4.1 4.1 0 0 1-4-4Z" />
  </svg>
)

const CHANNELS: Channel[] = [
  {
    key: 'x',
    label: 'Post on X',
    icon: XIcon,
    href: (url, text) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  {
    key: 'facebook',
    label: 'Share',
    icon: FacebookIcon,
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    key: 'discord',
    label: 'Discord',
    icon: DiscordIcon,
    // Discord has no share-intent URL, so this opens the app/web client; the
    // link is already on the clipboard-ready field below for pasting.
    href: () => 'https://discord.com/channels/@me',
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    icon: TikTokIcon,
    // TikTok has no web share intent either — posting happens in-app, so this
    // opens upload and relies on the copied link.
    href: () => 'https://www.tiktok.com/upload',
  },
  {
    key: 'email',
    label: 'Email',
    icon: <Mail className="size-5" />,
    href: (url, text) =>
      `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
    sameTab: true,
  },
  {
    key: 'sms',
    label: 'Text',
    icon: <MessageCircle className="size-5" />,
    href: (url, text) => `sms:?&body=${encodeURIComponent(`${text} ${url}`)}`,
    sameTab: true,
  },
]

export function InviteModal({
  t,
  open,
  onClose,
}: {
  t: Tournament
  open: boolean
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  const [url, setUrl] = useState('')
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  // window.location is client-only, so read it once the modal is actually open.
  useEffect(() => {
    if (open && typeof window !== 'undefined') setUrl(window.location.href)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    // The overlay covers the viewport now that it portals to body, so stop the
    // page behind it from scrolling under the modal.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open || !mounted) return null

  const text = `Join me in ${t.title} on GamerPlug`

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      /* clipboard unavailable */
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Invite a friend"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-t-2xl border border-border bg-card p-6 duration-200 animate-in fade-in-0 slide-in-from-bottom-4 sm:rounded-2xl"
      >
        <div className="flex items-start justify-between">
          <div className="flex size-12 items-center justify-center rounded-full bg-secondary">
            <Share2 className="size-5 text-foreground" />
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>

        <h2 className="mt-4 text-xl font-bold">Invite a Friend</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
          It&apos;s always more fun with a squad. Send them the link and lock in your
          roster before the lobby fills up.
        </p>

        <div className="mt-6 grid grid-cols-4 gap-x-2 gap-y-5 sm:grid-cols-6">
          {CHANNELS.map((c) => (
            <a
              key={c.key}
              href={c.href(url, text)}
              {...(c.sameTab ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
              className="group flex flex-col items-center gap-2 text-center"
            >
              <span className="flex size-12 items-center justify-center rounded-full bg-secondary text-foreground transition-colors group-hover:bg-secondary/70">
                {c.icon}
              </span>
              <span className="text-xs text-muted-foreground transition-colors group-hover:text-foreground">
                {c.label}
              </span>
            </a>
          ))}
        </div>

        <div className="mt-6 border-t border-border pt-5">
          <p className="mb-2 text-sm font-medium">Share the link:</p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={url}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Event link"
              className="h-10 min-w-0 flex-1 rounded-xl border border-border bg-background px-3.5 text-sm text-muted-foreground outline-none focus:border-primary"
            />
            <button
              onClick={copy}
              className={cn(
                'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border border-border px-4 text-sm font-semibold transition-colors',
                copied
                  ? 'border-emerald-500/40 text-emerald-400'
                  : 'hover:border-primary/50'
              )}
            >
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
